import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import userService from '../../services/userService';
import Button from '../../components/common/Button';
import { useToast } from '../../hooks/useToast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPlay, faCrosshairs } from '@fortawesome/free-solid-svg-icons';

export default function SuperAdminSegmentation() {
  const { showToast } = useToast();
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [variables, setVariables] = useState([]);
  const [intersectionUsers, setIntersectionUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState('');
  const [threshold, setThreshold] = useState('5.0');
  
  const [stats, setStats] = useState(null);
  const [overlapMatrix, setOverlapMatrix] = useState(null);
  
  const [activeTab, setActiveTab] = useState('categories');
  const [loading, setLoading] = useState(true);

  // Form states
  const [catName, setCatName] = useState('');
  const [catId, setCatId] = useState('');
  const [catDesc, setCatDesc] = useState('');
  const [catVars, setCatVars] = useState({});

  const [groupName, setGroupName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [groupDesc, setGroupDesc] = useState('');
  const [groupCats, setGroupCats] = useState([]);

  const [varKey, setVarKey] = useState('');
  const [varLabel, setVarLabel] = useState('');
  const [varType, setVarType] = useState('number');
  const [varDefault, setVarDefault] = useState('1.0');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [catRes, grpRes, varRes, statsRes, matrixRes] = await Promise.all([
        userService.getCategories(),
        userService.getCategoryGroups(),
        userService.getUniversalVariables(),
        userService.getSegmentationStats(),
        userService.getSegmentationOverlapMatrix()
      ]);
      setCategories(catRes.data?.data?.items || catRes.data?.data || []);
      setGroups(grpRes.data?.data?.items || grpRes.data?.data || []);
      setVariables(varRes.data?.data || []);
      setStats(statsRes.data?.data || null);
      setOverlapMatrix(matrixRes.data?.data || null);
    } catch (err) {
      showToast('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      await userService.createCategory({
        category_id: catId,
        name: catName,
        description: catDesc,
        status: 'active',
        segmentation_variables: catVars
      });
      showToast('Category created', 'success');
      setCatName('');
      setCatId('');
      setCatDesc('');
      setCatVars({});
      loadAll();
    } catch {
      showToast('Failed to create category', 'error');
    }
  };

  const handleAddGroup = async (e) => {
    e.preventDefault();
    try {
      await userService.createCategoryGroup({
        group_id: groupId,
        name: groupName,
        description: groupDesc,
        category_ids: groupCats,
        status: 'active'
      });
      showToast('Category group created', 'success');
      setGroupName('');
      setGroupId('');
      setGroupDesc('');
      setGroupCats([]);
      loadAll();
    } catch {
      showToast('Failed to create group', 'error');
    }
  };

  const handleAddVar = async (e) => {
    e.preventDefault();
    try {
      await userService.createUniversalVariable({
        variable_key: varKey,
        label: varLabel,
        data_type: varType,
        default_value: parseFloat(varDefault) || varDefault
      });
      showToast('Universal variable registered', 'success');
      setVarKey('');
      setVarLabel('');
      setVarDefault('1.0');
      loadAll();
    } catch {
      showToast('Failed to register variable', 'error');
    }
  };

  const handleTriggerCalc = async () => {
    try {
      await userService.triggerSegmentation();
      showToast('Background segment calculation triggered', 'success');
    } catch {
      showToast('Failed to trigger calculation', 'error');
    }
  };

  const handleQueryIntersection = async (e) => {
    e.preventDefault();
    try {
      const res = await userService.getSegmentationIntersection({
        groups: selectedGroups,
        threshold: parseFloat(threshold) || 5.0
      });
      setIntersectionUsers(res.data?.data || []);
      showToast('Intersection results retrieved', 'success');
    } catch {
      showToast('Failed to query intersection', 'error');
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await userService.deleteCategory(id);
      showToast('Category deleted', 'success');
      loadAll();
    } catch { showToast('Failed to delete', 'error'); }
  };
  const handleDeleteGroup = async (id) => {
    try {
      await userService.deleteCategoryGroup(id);
      showToast('Group deleted', 'success');
      loadAll();
    } catch { showToast('Failed to delete', 'error'); }
  };
  const handleDeleteVariable = async (key) => {
    try {
      await userService.deleteUniversalVariable(key);
      showToast('Variable deleted', 'success');
      loadAll();
    } catch { showToast('Failed to delete', 'error'); }
  };

  return (
    <DashboardLayout>
      <div style={{ padding: '24px 20px', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontWeight: 700, color: '#1f2937' }}>User Segmentation & Categories</h2>
          <Button variant="primary" onClick={handleTriggerCalc}>
            <FontAwesomeIcon icon={faPlay} style={{ marginRight: 8 }} />
            Run Segmentation Engine
          </Button>
        </div>

        {/* Tab Headers */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 24, gap: 16 }}>
          {['categories', 'groups', 'intersection'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === tab ? '#3b82f6' : '#6b7280',
                padding: '8px 16px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                textTransform: 'capitalize'
              }}
            >
              {tab === 'intersection' ? 'Intersection Analysis' : tab === 'groups' ? 'Category Groups' : 'Categories & Variables'}
            </button>
          ))}
        </div>

        {loading ? <p>Loading...</p> : (
          <div>
            {/* TAB 1: CATEGORIES & UNIVERSAL VARIABLES */}
            {activeTab === 'categories' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                <div>
                  <h3 style={{ marginTop: 0 }}>Add New Art Category</h3>
                  <form onSubmit={handleAddCategory} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <input required placeholder="Category Slug ID (e.g. oil-painting)" value={catId} onChange={(e) => setCatId(e.target.value)}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }} />
                    <input required placeholder="Category Name" value={catName} onChange={(e) => setCatName(e.target.value)}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }} />
                    <textarea placeholder="Description" value={catDesc} onChange={(e) => setCatDesc(e.target.value)}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db', height: 80 }} />
                    
                    {variables.length > 0 && (
                      <div style={{ padding: 12, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: 13 }}>Universal Plane Variables</h4>
                        {variables.map((v) => (
                          <div key={v.variable_key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <span style={{ fontSize: 12 }}>{v.label}</span>
                            <input
                              placeholder={`Default: ${v.default_value}`}
                              onChange={(e) => setCatVars({ ...catVars, [v.variable_key]: parseFloat(e.target.value) || e.target.value })}
                              style={{ width: 100, padding: 6, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12 }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    <Button variant="primary" type="submit">Create Category</Button>
                  </form>

                  <h3 style={{ marginTop: 24 }}>Register Universal Plane Variable</h3>
                  <form onSubmit={handleAddVar} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <input required placeholder="Variable Key (e.g. liquidity_multiplier)" value={varKey} onChange={(e) => setVarKey(e.target.value)}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }} />
                    <input required placeholder="Display Label (e.g. Liquidity Multiplier)" value={varLabel} onChange={(e) => setVarLabel(e.target.value)}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }} />
                    <input placeholder="Default Value" value={varDefault} onChange={(e) => setVarDefault(e.target.value)}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }} />
                    <Button variant="secondary" type="submit">Register Variable</Button>
                  </form>

                  <h3 style={{ marginTop: 24 }}>Active Variables</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {variables.map((v) => (
                      <div key={v.variable_key} style={{ padding: '8px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{v.label}</div>
                          <div style={{ fontSize: 11, color: '#6b7280' }}>Key: {v.variable_key} | Default: {v.default_value}</div>
                        </div>
                        <button onClick={() => handleDeleteVariable(v.variable_key)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>Delete</button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 style={{ marginTop: 0 }}>Active Categories</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {categories.map((c) => (
                      <div key={c.category_id} style={{ padding: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h4 style={{ margin: 0 }}>{c.name}</h4>
                          <div>
                            <span style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 10, marginRight: 8 }}>{c.category_id}</span>
                            <button onClick={() => handleDeleteCategory(c.category_id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>Delete</button>
                          </div>
                        </div>
                        <p style={{ margin: '6px 0', fontSize: 13, color: '#4b5563' }}>{c.description}</p>
                        {c.segmentation_variables && Object.keys(c.segmentation_variables).length > 0 && (
                          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {Object.entries(c.segmentation_variables).map(([k, v]) => (
                              <span key={k} style={{ fontSize: 11, background: '#f3f4f6', color: '#374151', padding: '2px 6px', borderRadius: 4 }}>
                                {k}: {v}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* TAB 2: CATEGORY GROUPS */}
            {activeTab === 'groups' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                <div>
                  <h3 style={{ marginTop: 0 }}>Create Category Group</h3>
                  <form onSubmit={handleAddGroup} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <input required placeholder="Group Slug ID (e.g. classical-arts)" value={groupId} onChange={(e) => setGroupId(e.target.value)}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }} />
                    <input required placeholder="Group Name" value={groupName} onChange={(e) => setGroupName(e.target.value)}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }} />
                    <textarea placeholder="Description" value={groupDesc} onChange={(e) => setGroupDesc(e.target.value)}
                      style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db', height: 80 }} />
                    
                    <div>
                      <label style={{ display: 'block', fontSize: 13, marginBottom: 6, fontWeight: 500 }}>Select Categories to Include</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 150, overflowY: 'auto', padding: 8, border: '1px solid #d1d5db', borderRadius: 8 }}>
                        {categories.map((c) => (
                          <label key={c.category_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                            <input
                              type="checkbox"
                              checked={groupCats.includes(c.category_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setGroupCats([...groupCats, c.category_id]);
                                } else {
                                  setGroupCats(groupCats.filter((id) => id !== c.category_id));
                                }
                              }}
                            />
                            {c.name}
                          </label>
                        ))}
                      </div>
                    </div>

                    <Button variant="primary" type="submit">Create Group</Button>
                  </form>
                </div>

                <div>
                  <h3 style={{ marginTop: 0 }}>Segment Groups</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {groups.map((g) => (
                      <div key={g.group_id} style={{ padding: 16, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h4 style={{ margin: 0 }}>{g.name}</h4>
                          <button onClick={() => handleDeleteGroup(g.group_id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 }}>Delete</button>
                        </div>
                        <p style={{ margin: '6px 0', fontSize: 13, color: '#4b5563' }}>{g.description}</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                          {g.category_ids?.map((cid) => (
                            <span key={cid} style={{ fontSize: 11, background: '#eff6ff', color: '#1d4ed8', padding: '2px 8px', borderRadius: 12 }}>
                              {cid}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* TAB 3: INTERSECTION & SEGMENT ANALYSIS */}
            {activeTab === 'intersection' && (
              <div>
                <h3 style={{ marginTop: 0 }}>Query User Intersections & Cross-sections</h3>
                <form onSubmit={handleQueryIntersection} style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 24 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Group IDs (Comma-separated)</label>
                    <input required placeholder="e.g. classical-arts,digital-collectibles" value={selectedGroups} onChange={(e) => setSelectedGroups(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Affinity Threshold</label>
                    <input type="number" step="0.1" value={threshold} onChange={(e) => setThreshold(e.target.value)}
                      style={{ width: 120, padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db' }} />
                  </div>
                  <Button variant="primary" type="submit">
                    <FontAwesomeIcon icon={faCrosshairs} style={{ marginRight: 8 }} />
                    Query Overlaps
                  </Button>
                </form>

                {intersectionUsers.length > 0 ? (
                  <div>
                    <h4>Overlapping Users ({intersectionUsers.length})</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                      <thead>
                        <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                          <th style={{ padding: 12, borderBottom: '1px solid #e5e7eb' }}>Name</th>
                          <th style={{ padding: 12, borderBottom: '1px solid #e5e7eb' }}>Email</th>
                          <th style={{ padding: 12, borderBottom: '1px solid #e5e7eb' }}>Affinity Scores</th>
                        </tr>
                      </thead>
                      <tbody>
                        {intersectionUsers.map((u) => (
                          <tr key={u.user_id}>
                            <td style={{ padding: 12, borderBottom: '1px solid #e5e7eb' }}>{u.name}</td>
                            <td style={{ padding: 12, borderBottom: '1px solid #e5e7eb' }}>{u.email}</td>
                            <td style={{ padding: 12, borderBottom: '1px solid #e5e7eb' }}>
                              {Object.entries(u.scores || {}).map(([k, v]) => (
                                <span key={k} style={{ fontSize: 11, background: '#f3f4f6', color: '#1f2937', padding: '2px 6px', borderRadius: 4, marginRight: 6 }}>
                                  {k}: {v}
                                </span>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p style={{ color: '#6b7280' }}>No users match the overlapping criteria or query not run yet.</p>
                )}

                <hr style={{ margin: '32px 0', borderColor: '#e5e7eb' }} />
                
                <h3 style={{ marginTop: 0 }}>Segmentation Distribution Stats</h3>
                {stats && (
                  <div style={{ marginBottom: 24, padding: 16, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                    <p>Total Segmented Investors: <strong>{stats.total_segmented_users}</strong></p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
                      {Object.entries(stats.distribution || {}).map(([seg, pct]) => (
                        <div key={seg} style={{ padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #d1d5db' }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>{seg}</span>
                          <div style={{ fontSize: 18, color: '#0369a1', marginTop: 4 }}>{pct}%</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <h3 style={{ marginTop: 0 }}>Group Overlap Matrix</h3>
                {overlapMatrix && overlapMatrix.groups && overlapMatrix.matrix && (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={{ padding: 8, border: '1px solid #e5e7eb', background: '#f3f4f6' }}>Groups</th>
                          {overlapMatrix.groups.map(g => (
                            <th key={g.group_id} style={{ padding: 8, border: '1px solid #e5e7eb', background: '#f3f4f6', minWidth: 100 }}>{g.name}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {overlapMatrix.groups.map(g1 => (
                          <tr key={g1.group_id}>
                            <td style={{ padding: 8, border: '1px solid #e5e7eb', fontWeight: 600, background: '#f3f4f6' }}>{g1.name}</td>
                            {overlapMatrix.groups.map(g2 => {
                              const data = overlapMatrix.matrix[g1.group_id]?.[g2.group_id];
                              return (
                                <td key={g2.group_id} style={{ padding: 8, border: '1px solid #e5e7eb', textAlign: 'center' }}>
                                  {data ? (
                                    <>
                                      <div style={{ fontWeight: g1.group_id === g2.group_id ? 700 : 400 }}>{data.count} users</div>
                                      <div style={{ fontSize: 11, color: '#6b7280' }}>({data.percentage}%)</div>
                                    </>
                                  ) : '-'}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
